import { Component } from '@theme/component';
import { Splide } from '@theme/splide';

/**
 * Unified Splide slider web component.
 *
 * Configuration is read from a `<script type="application/json">` child element,
 * so each instance can be fully configured from Liquid without touching JS.
 *
 * Supported config keys (all optional – sensible defaults are provided):
 *   type, arrows, pagination, focus, mediaQuery, gap, perPage,
 *   destroyAt, initBelow, paginationClass, pageClass, breakpoints
 *
 * `destroyAt`  – destroy slider when viewport is AT OR ABOVE this breakpoint (mobile-first).
 * `initBelow`  – only initialise slider when viewport is BELOW this breakpoint (same effect,
 *                provided for readability). `destroyAt` takes precedence when both are set.
 * 
 * @typedef {Object} SplideSliderConfig
 * @property {string} [type] - Slider type: 'loop' | 'slide' | 'fade'
 * @property {boolean} [arrows] - Show navigation arrows
 * @property {boolean} [pagination] - Show pagination dots
 * @property {string} [focus] - Focus position
 * @property {string} [mediaQuery] - Media query direction: 'min' | 'max'
 * @property {number} [gap] - Gap between slides in px
 * @property {number} [perPage] - Number of slides per page
 * @property {number} [destroyAt] - Viewport width (px) at which to destroy the slider (mobile-up)
 * @property {number} [initBelow] - Viewport width (px) above which the slider should NOT init (desktop-down)
 * @property {string} [paginationClass] - Extra class(es) for the pagination list
 * @property {string} [pageClass] - Extra class(es) for each pagination button
 * @property {Object} [breakpoints] - Splide breakpoints object
 * 
 * @typedef {Object} Refs
 * @property {HTMLScriptElement} config
 *
 * @extends {Component<Refs>}
 *
 * @example Liquid usage
 * <splide-slider class="splide">
 *   <script type="application/json">
 *     {
 *       "type": "loop",
 *       "gap": 16,
 *       "pagination": true,
 *       "paginationClass": "my-pagination",
 *       "pageClass": "my-page",
 *       "destroyAt": 1000,
 *       "breakpoints": { "576": { "perPage": 2 } }
 *     }
 *   </script>
 *   <div class="splide__track">
 *     <ul class="splide__list">…</ul>
 *   </div>
 * </splide-slider>
 */
class SplideSlider extends Component {
  /** @type {Splide|null} */
  #slider = null;

  /** @type {MediaQueryList|null} */
  #mq = null;

  /** @type {((mq: MediaQueryListEvent) => void)|null} */
  #onMqChange = null;

  requiredRefs = ['config'];

  connectedCallback() {
    super.connectedCallback();

    const config = this.#readConfig();
    const breakpoint = config.destroyAt ?? config.initBelow ?? null;

    if (breakpoint !== null) {
      this.#mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
      this.#onMqChange = (e) => this.#handleBreakpoint(e.matches, config);
      this.#mq.addEventListener('change', this.#onMqChange);
      this.#handleBreakpoint(this.#mq.matches, config);
    } else {
      this.#initSlider(config);
    }
  }

  disconnectedCallback() {
    if (this.#mq && this.#onMqChange) {
      this.#mq.removeEventListener('change', this.#onMqChange);
    }

    this.#destroySlider();
    super.disconnectedCallback?.();
  }

  /**
   * Reads the JSON config from the first `<script type="application/json">` child.
   * Falls back to an empty object when absent or malformed.
   *
   * @returns {SplideSliderConfig}
   */
  #readConfig() {
    const configEl = this.refs.config;

    try {
      return JSON.parse(configEl.textContent ?? '{}');
    } catch {
      return {};
    }
  }

  /**
   * Builds the Splide options object from the parsed config, applying defaults.
   *
   * @param {SplideSliderConfig} config
   * @returns {import('@theme/splide').Options}
   */
  #buildOptions(config) {
    const direction = document.dir || 'ltr';

    const paginationClass = [
      'splide__pagination',
      'slider__pagination',
      config.paginationClass,
    ].filter(Boolean).join(' ');

    const pageClass = [
      'splide__pagination__page',
      'slider__pagination-button',
      config.pageClass,
    ].filter(Boolean).join(' ');

    const options = {
      type: config.type ?? 'loop',
      arrows: config.arrows ?? false,
      pagination: config.pagination ?? true,
      focus: config.focus,
      mediaQuery: config.mediaQuery ?? 'min',
      gap: config.gap ?? 8,
      direction,
      classes: {
        pagination: paginationClass,
        page: pageClass,
      },
    };

    if (config.perPage !== undefined) options.perPage = config.perPage;
    if (config.breakpoints !== undefined) options.breakpoints = config.breakpoints;

    return options;
  }

  /**
   * Initialises the Splide instance and marks the element as active.
   *
   * @param {SplideSliderConfig} config
   */
  #initSlider(config) {
    if (this.#slider) return;

    this.#slider = new Splide(this, this.#buildOptions(config));
    this.#slider.mount();
    this.setAttribute('data-active', '');
  }

  /**
   * Destroys the Splide instance and removes the active marker.
   */
  #destroySlider() {
    if (!this.#slider) return;

    this.#slider.destroy();
    this.#slider = null;
    this.removeAttribute('data-active');
  }

  /**
   * Responds to a media-query change event.
   * When the breakpoint matches (viewport is wide enough) the slider is destroyed;
   * when it no longer matches the slider is initialised.
   *
   * @param {boolean} matches - Whether the media query currently matches
   * @param {SplideSliderConfig} config
   */
  #handleBreakpoint(matches, config) {
    if (matches) {
      this.#destroySlider();
    } else {
      this.#initSlider(config);
    }
  }
}

customElements.define('splide-slider', SplideSlider);
